import React, { useState, useEffect, useMemo } from 'react';
import { CalendarProps } from '../types/calendar';
import { 
  generateCalendarWeek, 
  generateCalendarDay,
  groupWorkoutsByDayAndHour, 
  getPreviousWeek, 
  getNextWeek, 
  getCurrentWeek,
  updateCurrentHour
} from '../utils/calendarUtils';
import CalendarNavigation from './CalendarNavigation';
import CalendarDay from './CalendarDay';
import { Workout } from '../types';

const WeeklyCalendar: React.FC<CalendarProps> = ({ 
  workouts, 
  isLoading = false, 
  onWorkoutClick,
  onMultipleWorkoutsClick,
  onHourClick
}) => {
  const [currentWeek, setCurrentWeek] = useState(() => getCurrentWeek());
  const [currentDay, setCurrentDay] = useState(() => new Date()); // Для мобильной версии
  const [isMobile, setIsMobile] = useState(false);

  // Определение мобильной версии
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Обновление текущего часа каждую минуту
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWeek(prev => updateCurrentHour(prev));
    }, 60000); // Обновляем каждую минуту

    return () => clearInterval(interval);
  }, []);

  // Обработчики навигации для десктопа (неделя)
  const handlePreviousWeek = () => {
    setCurrentWeek(prev => getPreviousWeek(prev));
  };

  const handleNextWeek = () => {
    setCurrentWeek(prev => getNextWeek(prev));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentWeek(getCurrentWeek());
    setCurrentDay(today);
  };

  // Обработчики навигации для мобильной версии (день)
  const handlePreviousDay = () => {
    setCurrentDay(prev => {
      const newDay = new Date(prev);
      newDay.setDate(newDay.getDate() - 1);
      return newDay;
    });
  };

  const handleNextDay = () => {
    setCurrentDay(prev => {
      const newDay = new Date(prev);
      newDay.setDate(newDay.getDate() + 1);
      return newDay;
    });
  };

  // Группировка тренировок по дням и часам для десктопа
  const weekWithWorkouts = useMemo(() => {
    // Убираем дубликаты тренировок по ID перед группировкой
    const uniqueWorkouts = workouts.filter((workout, index, self) => 
      index === self.findIndex(w => w.id === workout.id)
    );
    
    const weekWithWorkoutsData = groupWorkoutsByDayAndHour(uniqueWorkouts, currentWeek);
    return updateCurrentHour(weekWithWorkoutsData);
  }, [workouts, currentWeek]);

  // Данные для текущего дня (мобильная версия)
  const currentDayData = useMemo(() => {
    if (!isMobile) return null;

    // Убираем дубликаты тренировок по ID перед фильтрацией
    const uniqueWorkouts = workouts.filter((workout, index, self) => 
      index === self.findIndex(w => w.id === workout.id)
    );

    // Фильтруем тренировки только для текущего дня
    const dayStart = new Date(currentDay);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(currentDay);
    dayEnd.setHours(23, 59, 59, 999);

    const dayWorkouts = uniqueWorkouts.filter(workout => {
      const workoutDate = new Date(workout.date);
      return workoutDate >= dayStart && workoutDate <= dayEnd;
    });

    // Создаем структуру для одного дня
    const dayStructure = generateCalendarDay(currentDay);
    const dayWithWorkouts = groupWorkoutsByDayAndHour(dayWorkouts, dayStructure);
    return updateCurrentHour(dayWithWorkouts);
  }, [workouts, currentDay, isMobile]);

  // Подсчет количества тренировок
  const workoutsCount = useMemo(() => {
    if (isMobile) {
      // Для мобильной версии считаем тренировки за день
      const dayStart = new Date(currentDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDay);
      dayEnd.setHours(23, 59, 59, 999);
      
      return workouts.filter(workout => {
        const workoutDate = new Date(workout.date);
        return workoutDate >= dayStart && workoutDate <= dayEnd;
      }).length;
    } else {
      // Для десктопа считаем тренировки за неделю
      const weekStart = currentWeek.startDate;
      const weekEnd = currentWeek.endDate;
      
      return workouts.filter(workout => {
        const workoutDate = new Date(workout.date);
        return workoutDate >= weekStart && workoutDate <= weekEnd;
      }).length;
    }
  }, [workouts, currentWeek, currentDay, isMobile]);

  // Обработчик клика по тренировке
  const handleWorkoutClick = (workout: Workout) => {
    if (onWorkoutClick) {
      onWorkoutClick(workout);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Навигация */}
      <CalendarNavigation
        currentWeek={isMobile && currentDayData ? currentDayData : weekWithWorkouts}
        onPreviousWeek={isMobile ? handlePreviousDay : handlePreviousWeek}
        onNextWeek={isMobile ? handleNextDay : handleNextWeek}
        onToday={handleToday}
        workoutsCount={workoutsCount}
        isMobile={isMobile}
      />

      {/* Календарь */}
      <div className="overflow-x-auto">
        {isMobile ? (
          /* Мобильная версия - только один день */
          currentDayData && currentDayData.days.length > 0 ? (
            <div className="w-full">
              <CalendarDay
                day={currentDayData.days[0]}
                onWorkoutClick={handleWorkoutClick}
                onMultipleWorkoutsClick={onMultipleWorkoutsClick}
                onHourClick={onHourClick}
                isMobile={true}
              />
            </div>
          ) : null
        ) : (
          /* Десктопная версия - 7 дней в ряд */
          <div className="flex">
            {weekWithWorkouts.days.map((day, index) => (
              <div key={day.date.toISOString()} className="flex-1 relative">
                <CalendarDay
                  day={day}
                  onWorkoutClick={handleWorkoutClick}
                  onMultipleWorkoutsClick={onMultipleWorkoutsClick}
                  onHourClick={onHourClick}
                  isMobile={false}
                />
                {index < weekWithWorkouts.days.length - 1 && (
                  <div className="absolute top-0 right-0 w-px h-full bg-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Пустое состояние */}
      {workouts.length === 0 && !isLoading && (
        <div className="p-8 text-center text-gray-500">
          <div className="text-lg font-medium mb-2">Нет тренировок на эту неделю</div>
          <div className="text-sm">Создайте новую тренировку, чтобы увидеть её в календаре</div>
        </div>
      )}
    </div>
  );
};

export default WeeklyCalendar; 